// src/config/standards/digitalLiteracy.js

// Digital Literacy Standards — structured for your app’s schema.
// Domains (kept distinct from Digital Citizenship):
//   1) Device Operations & Management
//   2) Productivity & Content Creation
//   3) Research & Information Literacy
//   4) Communication & Collaboration
//   5) Data & Visualization
//   6) Accessibility & Inclusive Use
//   7) Troubleshooting & Systems
//
// Grade bands:
//   K–2 share one band
//   3–5 share one band
//   6–8 share one band
//   High School clusters: "HS—9–10" and "HS—11–12" under each domain
//
// NOTE: Descriptions are original summaries aligned with widely used U.S. K–12
// digital literacy frameworks (e.g., ISTE-aligned skills), not verbatim text.

const dlItem = (grade, code, description) => ({
  id: `Digital Literacy-${grade}-${code}`,
  code,
  description,
});

// ----------------------------------
// K–2
// ----------------------------------
const K2 = {
  "Device Operations & Management": [
    dlItem("K–2", "DL.K-2.DOM.1", "Identify basic device parts (screen, keyboard, mouse, camera) and demonstrate safe handling."),
    dlItem("K–2", "DL.K-2.DOM.2", "Open, close, and switch between teacher-assigned apps; log in/out with adult help."),
    dlItem("K–2", "DL.K-2.DOM.3", "Save and find a file in a teacher-designated location (e.g., class folder)."),
  ],
  "Productivity & Content Creation": [
    dlItem("K–2", "DL.K-2.PC.1", "Create simple documents with text and images (e.g., a labeled picture or sentence)."),
    dlItem("K–2", "DL.K-2.PC.2", "Use drawing or voice tools to share ideas; add a title to a project."),
    dlItem("K–2", "DL.K-2.PC.3", "Insert teacher-provided images or shapes; adjust size and position."),
  ],
  "Research & Information Literacy": [
    dlItem("K–2", "DL.K-2.RIL.1", "With guidance, use a curated search box or collection to find information on a topic."),
    dlItem("K–2", "DL.K-2.RIL.2", "Ask, “Who made this?” and “What is it for?” when viewing a website or video."),
    dlItem("K–2", "DL.K-2.RIL.3", "Sort information or pictures into simple categories with adult support."),
  ],
  "Communication & Collaboration": [
    dlItem("K–2", "DL.K-2.CC.1", "Use classroom-approved tools to share work with the teacher or partners."),
    dlItem("K–2", "DL.K-2.CC.2", "Record or type a short message with a greeting and your name."),
    dlItem("K–2", "DL.K-2.CC.3", "Participate in a small-group digital activity by taking turns and listening."),
  ],
  "Data & Visualization": [
    dlItem("K–2", "DL.K-2.DV.1", "Collect simple class data (e.g., favorite fruit) and tally results."),
    dlItem("K–2", "DL.K-2.DV.2", "Create a picture graph or bar chart with teacher support."),
    dlItem("K–2", "DL.K-2.DV.3", "Explain what a graph shows in one or two sentences."),
  ],
  "Accessibility & Inclusive Use": [
    dlItem("K–2", "DL.K-2.ACC.1", "Use basic accessibility features when prompted (zoom, read-aloud, voice input)."),
    dlItem("K–2", "DL.K-2.ACC.2", "Follow class norms for sharing devices and helping classmates."),
    dlItem("K–2", "DL.K-2.ACC.3", "Choose readable fonts and simple layouts for classmates to understand."),
  ],
  "Troubleshooting & Systems": [
    dlItem("K–2", "DL.K-2.TR.1", "Try a simple fix (check volume, cables, battery) before asking for help."),
    dlItem("K–2", "DL.K-2.TR.2", "Restart an app or device with teacher guidance when it freezes."),
    dlItem("K–2", "DL.K-2.TR.3", "Explain the problem clearly to an adult (what you clicked, what happened)."),
  ],
};

// ----------------------------------
// 3–5
// ----------------------------------
const G3_5 = {
  "Device Operations & Management": [
    dlItem("3–5", "DL.3-5.DOM.1", "Organize files into named folders; use consistent file names and versions."),
    dlItem("3–5", "DL.3-5.DOM.2", "Adjust system settings (Wi-Fi, display, audio) with teacher permission."),
    dlItem("3–5", "DL.3-5.DOM.3", "Explain cloud vs. local storage and when to use each."),
  ],
  "Productivity & Content Creation": [
    dlItem("3–5", "DL.3-5.PC.1", "Produce a multi-page document with headings, lists, and inserted images/tables."),
    dlItem("3–5", "DL.3-5.PC.2", "Create a short slide deck with clear titles, concise text, and visuals."),
    dlItem("3–5", "DL.3-5.PC.3", "Record and edit basic audio or screencast to explain a process."),
  ],
  "Research & Information Literacy": [
    dlItem("3–5", "DL.3-5.RIL.1", "Use keywords and filters to narrow search results to relevant sources."),
    dlItem("3–5", "DL.3-5.RIL.2", "Identify the author and purpose of a source; distinguish facts from opinions."),
    dlItem("3–5", "DL.3-5.RIL.3", "Cite sources in a simple format (title/author/link) when using information."),
  ],
  "Communication & Collaboration": [
    dlItem("3–5", "DL.3-5.CC.1", "Use shared documents to co-author, comment, and track changes."),
    dlItem("3–5", "DL.3-5.CC.2", "Choose an appropriate tool (email, chat, comment) for a specific task."),
    dlItem("3–5", "DL.3-5.CC.3", "Apply clear subject lines and complete sentences in messages."),
  ],
  "Data & Visualization": [
    dlItem("3–5", "DL.3-5.DV.1", "Enter data in a spreadsheet; use basic formulas (sum, average)."),
    dlItem("3–5", "DL.3-5.DV.2", "Create charts (bar, line, pie) that match the data and label them clearly."),
    dlItem("3–5", "DL.3-5.DV.3", "Explain what the data shows and any simple patterns or trends."),
  ],
  "Accessibility & Inclusive Use": [
    dlItem("3–5", "DL.3-5.ACC.1", "Use headings, alt text, and readable color contrast in documents."),
    dlItem("3–5", "DL.3-5.ACC.2", "Apply captions or transcripts for videos you create when possible."),
    dlItem("3–5", "DL.3-5.ACC.3", "Select templates/layouts that improve clarity for all audiences."),
  ],
  "Troubleshooting & Systems": [
    dlItem("3–5", "DL.3-5.TR.1", "Follow a simple troubleshooting checklist (reload, reconnect, restart)."),
    dlItem("3–5", "DL.3-5.TR.2", "Install updates with permission; report issues using a clear description."),
    dlItem("3–5", "DL.3-5.TR.3", "Explain how peripherals (printers, cameras) connect and function."),
  ],
};

// ----------------------------------
// 6–8
// ----------------------------------
const G6_8 = {
  "Device Operations & Management": [
    dlItem("6–8", "DL.6-8.DOM.1", "Manage storage across devices/cloud; apply naming/versioning conventions."),
    dlItem("6–8", "DL.6-8.DOM.2", "Configure notifications and system preferences to support focus and workflow."),
    dlItem("6–8", "DL.6-8.DOM.3", "Compare platforms (web, desktop, mobile) for capability and constraints."),
  ],
  "Productivity & Content Creation": [
    dlItem("6–8", "DL.6-8.PC.1", "Plan, draft, and publish multimedia products integrating text, visuals, and audio."),
    dlItem("6–8", "DL.6-8.PC.2", "Apply layout and design principles (alignment, hierarchy, whitespace)."),
    dlItem("6–8", "DL.6-8.PC.3", "Use intermediate features (master slides, styles, mail merge, simple video edits)."),
  ],
  "Research & Information Literacy": [
    dlItem("6–8", "DL.6-8.RIL.1", "Formulate effective queries; evaluate credibility, currency, and bias."),
    dlItem("6–8", "DL.6-8.RIL.2", "Synthesize information from multiple sources; paraphrase and cite appropriately."),
    dlItem("6–8", "DL.6-8.RIL.3", "Track research with notes/bibliography tools; avoid plagiarism."),
  ],
  "Communication & Collaboration": [
    dlItem("6–8", "DL.6-8.CC.1", "Collaborate asynchronously and synchronously; use comments and tasks to manage work."),
    dlItem("6–8", "DL.6-8.CC.2", "Adapt tone and format for audience and purpose; follow netiquette."),
    dlItem("6–8", "DL.6-8.CC.3", "Plan and deliver a live or recorded presentation using speaker notes and visuals."),
  ],
  "Data & Visualization": [
    dlItem("6–8", "DL.6-8.DV.1", "Collect, clean, and organize datasets; choose appropriate data types."),
    dlItem("6–8", "DL.6-8.DV.2", "Use formulas, sorting, filtering; create comparative visualizations."),
    dlItem("6–8", "DL.6-8.DV.3", "Interpret trends/outliers and discuss limitations of the data."),
  ],
  "Accessibility & Inclusive Use": [
    dlItem("6–8", "DL.6-8.ACC.1", "Apply accessible authoring (alt text, headings, reading order, captions)."),
    dlItem("6–8", "DL.6-8.ACC.2", "Explain inclusive design principles and adjust content accordingly."),
    dlItem("6–8", "DL.6-8.ACC.3", "Evaluate peer work for accessibility and provide actionable feedback."),
  ],
  "Troubleshooting & Systems": [
    dlItem("6–8", "DL.6-8.TR.1", "Diagnose common issues (network, permissions, storage) using systematic steps."),
    dlItem("6–8", "DL.6-8.TR.2", "Document and escalate problems with clear reproduction steps and screenshots."),
    dlItem("6–8", "DL.6-8.TR.3", "Compare operating systems and app ecosystems at a basic conceptual level."),
  ],
};

// ----------------------------------
// HIGH SCHOOL CLUSTERS
// ----------------------------------
const HS_9_10 = {
  "Device Operations & Management": [
    dlItem("HS—9–10", "DL.HS-9-10.DOM.1", "Design a file and versioning strategy for multi-tool projects across devices."),
    dlItem("HS—9–10", "DL.HS-9-10.DOM.2", "Configure profiles, storage, and backups to protect work and ensure continuity."),
    dlItem("HS—9–10", "DL.HS-9-10.DOM.3", "Evaluate tradeoffs of web vs. native apps for performance, offline use, and sharing."),
  ],
  "Productivity & Content Creation": [
    dlItem("HS—9–10", "DL.HS-9-10.PC.1", "Produce publication-quality artifacts using advanced features (styles, references, master layouts)."),
    dlItem("HS—9–10", "DL.HS-9-10.PC.2", "Apply design critiques to iterate on clarity, aesthetics, and purpose alignment."),
    dlItem("HS—9–10", "DL.HS-9-10.PC.3", "Plan and execute video/podcast projects with scripting, editing, and credits."),
  ],
  "Research & Information Literacy": [
    dlItem("HS—9–10", "DL.HS-9-10.RIL.1", "Conduct sustained research with advanced search operators and database tools."),
    dlItem("HS—9–10", "DL.HS-9-10.RIL.2", "Evaluate conflicting sources; reconcile discrepancies with corroborating evidence."),
    dlItem("HS—9–10", "DL.HS-9-10.RIL.3", "Use citation managers; produce properly formatted references and in-text citations."),
  ],
  "Communication & Collaboration": [
    dlItem("HS—9–10", "DL.HS-9-10.CC.1", "Lead project management using shared workspaces, roles, and timelines."),
    dlItem("HS—9–10", "DL.HS-9-10.CC.2", "Tailor communication artifacts (briefs, updates, slide decks) to stakeholders."),
    dlItem("HS—9–10", "DL.HS-9-10.CC.3", "Host or facilitate virtual meetings effectively with prepared agendas and follow-ups."),
  ],
  "Data & Visualization": [
    dlItem("HS—9–10", "DL.HS-9-10.DV.1", "Model data with appropriate types; use multi-sheet workbooks and references."),
    dlItem("HS—9–10", "DL.HS-9-10.DV.2", "Create persuasive visualizations; justify chart choices and design decisions."),
    dlItem("HS—9–10", "DL.HS-9-10.DV.3", "Summarize insights and limitations; recommend data-driven next steps."),
  ],
  "Accessibility & Inclusive Use": [
    dlItem("HS—9–10", "DL.HS-9-10.ACC.1", "Audit digital products against basic accessibility guidelines and remediate issues."),
    dlItem("HS—9–10", "DL.HS-9-10.ACC.2", "Plan for multilingual or assistive-tech users; provide alternative formats."),
    dlItem("HS—9–10", "DL.HS-9-10.ACC.3", "Advocate for inclusive practices in team workflows and peer reviews."),
  ],
  "Troubleshooting & Systems": [
    dlItem("HS—9–10", "DL.HS-9-10.TR.1", "Apply systematic troubleshooting and root-cause analysis to resolve issues."),
    dlItem("HS—9–10", "DL.HS-9-10.TR.2", "Maintain devices with updates, storage hygiene, and basic security posture."),
    dlItem("HS—9–10", "DL.HS-9-10.TR.3", "Compare file formats and interoperability; choose formats for longevity and collaboration."),
  ],
};

const HS_11_12 = {
  "Device Operations & Management": [
    dlItem("HS—11–12", "DL.HS-11-12.DOM.1", "Design a portability/backup strategy (cloud sync, archives, handoff) for capstone work."),
    dlItem("HS—11–12", "DL.HS-11-12.DOM.2", "Evaluate device/platform ecosystems for specific professional or academic needs."),
    dlItem("HS—11–12", "DL.HS-11-12.DOM.3", "Document workflows and settings to onboard collaborators efficiently."),
  ],
  "Productivity & Content Creation": [
    dlItem("HS—11–12", "DL.HS-11-12.PC.1", "Produce professional-quality media or publications with brand-consistent design."),
    dlItem("HS—11–12", "DL.HS-11-12.PC.2", "Integrate feedback cycles and usability tests to improve clarity and impact."),
    dlItem("HS—11–12", "DL.HS-11-12.PC.3", "Package and license deliverables appropriately for target audiences and reuse."),
  ],
  "Research & Information Literacy": [
    dlItem("HS—11–12", "DL.HS-11-12.RIL.1", "Design and execute a research plan using scholarly databases and advanced query syntax."),
    dlItem("HS—11–12", "DL.HS-11-12.RIL.2", "Synthesize complex, conflicting evidence into a balanced, well-cited product."),
    dlItem("HS—11–12", "DL.HS-11-12.RIL.3", "Apply academic integrity standards; manage citations across multiple formats."),
  ],
  "Communication & Collaboration": [
    dlItem("HS—11–12", "DL.HS-11-12.CC.1", "Lead cross-role collaboration; manage permissions, versions, and approvals."),
    dlItem("HS—11–12", "DL.HS-11-12.CC.2", "Create executive-level summaries and visuals adapted to stakeholder needs."),
    dlItem("HS—11–12", "DL.HS-11-12.CC.3", "Produce polished presentations with narrative flow, evidence, and visual clarity."),
  ],
  "Data & Visualization": [
    dlItem("HS—11–12", "DL.HS-11-12.DV.1", "Prepare and analyze larger datasets; use advanced functions and validation."),
    dlItem("HS—11–12", "DL.HS-11-12.DV.2", "Design dashboards or multi-view visuals; justify choices using data-viz principles."),
    dlItem("HS—11–12", "DL.HS-11-12.DV.3", "Communicate actionable insights and acknowledge uncertainty/limitations."),
  ],
  "Accessibility & Inclusive Use": [
    dlItem("HS—11–12", "DL.HS-11-12.ACC.1", "Evaluate products with accessibility checkers and manual tests; remediate defects."),
    dlItem("HS—11–12", "DL.HS-11-12.ACC.2", "Integrate accessibility into planning, production, and peer review processes."),
    dlItem("HS—11–12", "DL.HS-11-12.ACC.3", "Advise peers on inclusive design tradeoffs and ethical considerations."),
  ],
  "Troubleshooting & Systems": [
    dlItem("HS—11–12", "DL.HS-11-12.TR.1", "Create troubleshooting guides and FAQs tailored to common user issues."),
    dlItem("HS—11–12", "DL.HS-11-12.TR.2", "Compare system architectures (local, cloud, hybrid) for reliability and access."),
    dlItem("HS—11–12", "DL.HS-11-12.TR.3", "Plan risk mitigation for projects (backups, format choices, handoff protocols)."),
  ],
};

// ----------------------------------
// Export full subject map
// ----------------------------------
export const DIGITAL_LITERACY_STANDARDS = {
  "Digital Literacy": {
    // Elementary bands
    "Kindergarten": K2,
    "1st Grade": K2,
    "2nd Grade": K2,

    "3rd Grade": G3_5,
    "4th Grade": G3_5,
    "5th Grade": G3_5,

    // Middle school band
    "6th Grade": G6_8,
    "7th Grade": G6_8,
    "8th Grade": G6_8,

    // High school clusters per domain
    "High School": {
      "Device Operations & Management": {
        "HS—9–10": HS_9_10["Device Operations & Management"],
        "HS—11–12": HS_11_12["Device Operations & Management"],
      },
      "Productivity & Content Creation": {
        "HS—9–10": HS_9_10["Productivity & Content Creation"],
        "HS—11–12": HS_11_12["Productivity & Content Creation"],
      },
      "Research & Information Literacy": {
        "HS—9–10": HS_9_10["Research & Information Literacy"],
        "HS—11–12": HS_11_12["Research & Information Literacy"],
      },
      "Communication & Collaboration": {
        "HS—9–10": HS_9_10["Communication & Collaboration"],
        "HS—11–12": HS_11_12["Communication & Collaboration"],
      },
      "Data & Visualization": {
        "HS—9–10": HS_9_10["Data & Visualization"],
        "HS—11–12": HS_11_12["Data & Visualization"],
      },
      "Accessibility & Inclusive Use": {
        "HS—9–10": HS_9_10["Accessibility & Inclusive Use"],
        "HS—11–12": HS_11_12["Accessibility & Inclusive Use"],
      },
      "Troubleshooting & Systems": {
        "HS—9–10": HS_9_10["Troubleshooting & Systems"],
        "HS—11–12": HS_11_12["Troubleshooting & Systems"],
      },
    },
  },
};

export default DIGITAL_LITERACY_STANDARDS;
